import React from 'react';

/**
 * 详情抽屉组件
 * 用于显示复杂数据结构的详细信息
 *
 * @param {Object} props - 组件属性
 * @param {boolean} props.isOpen - 是否打开抽屉
 * @param {Function} props.onClose - 关闭抽屉的回调函数
 * @param {Object} props.summary - 要显示的数据对象
 * @param {string} props.title - 抽屉标题，默认为 "Details"
 * @param {string} props.width - 抽屉宽度，默认为 "w-1/3"
 */
const DetailDrawer = ({ isOpen, onClose, summary, title = 'Details', width = 'w-1/3' }) => {
  if (!isOpen || !summary) return null;

  // 格式化日期时间戳
  const formatTimestamp = timestamp => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      // 格式化为 YYYY-MM-DD HH:mm:ss
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return timestamp;
    }
  };

  // 检测是否为新旧值对比格式的特殊对象
  const isVersionControlObject = obj => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return false;
    }
    // 检查是否包含 newvalue, oldvalue, time_stamp 这三个关键字段
    const hasNewValue = 'newvalue' in obj;
    const hasOldValue = 'oldvalue' in obj;
    const hasTimestamp = 'time_stamp' in obj;
    return hasNewValue && hasOldValue && hasTimestamp;
  };

  // 检测是否是字段更新对象（field_updates 数组中的对象）
  const isFieldUpdateObject = obj => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return false;
    }
    // 检查是否包含 field, old_value, new_value 这三个关键字段
    const hasField = 'field' in obj;
    const hasOldValue = 'old_value' in obj;
    const hasNewValue = 'new_value' in obj;
    return hasField && hasOldValue && hasNewValue;
  };

  // 检测是否是更新记录对象（field_updates 数组元素）
  const isUpdateRecord = obj => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return false;
    }
    return 'timestamp' in obj && ('summary' in obj || 'summary_field_changes' in obj);
  };

  // 递归格式化值的辅助函数
  const formatValue = (value, depth = 0) => {
    if (value === null || value === undefined) {
      return 'N/A';
    } else if (Array.isArray(value)) {
      if (value.length === 0) return 'None';

      // 检查是否是 field_updates 数组（包含更新记录）
      const isFieldUpdatesArray = value.some(item => isUpdateRecord(item));
      if (isFieldUpdatesArray) {
        return (
          <div className={`ml-${depth * 4} space-y-3`}>
            {value.map((item, index) => (
              <div key={index} className='border border-gray-200 rounded-lg p-3 bg-gray-50'>
                {/* 更新时间 */}
                {item.timestamp && (
                  <div className='text-sm font-semibold text-blue-600 mb-2'>⏰ {formatTimestamp(item.timestamp)}</div>
                )}

                {/* Summary 文本 */}
                {item.summary && <div className='text-sm text-gray-700 mb-2'>{item.summary}</div>}

                {/* Summary field changes */}
                {item.summary_field_changes && item.summary_field_changes.length > 0 && (
                  <div className='mt-2'>
                    <div className='text-xs font-semibold text-gray-600 mb-1'>Summary Fields:</div>
                    <div className='space-y-1'>
                      {item.summary_field_changes.map((change, idx) => (
                        <div key={idx} className='text-sm'>
                          <span className='font-medium'>{change.field}:</span>
                          <div className='ml-2 flex flex-wrap gap-2'>
                            {Array.isArray(change.old_value) &&
                              change.old_value.map((old, oldIdx) => (
                                <span key={`old-${oldIdx}`} className='line-through text-gray-400'>
                                  {old}
                                </span>
                              ))}
                            {!Array.isArray(change.old_value) && change.old_value && (
                              <span className='line-through text-gray-400'>{JSON.stringify(change.old_value)}</span>
                            )}
                            {Array.isArray(change.new_value) &&
                              change.new_value.map((newVal, newIdx) => (
                                <span key={`new-${newIdx}`} className='text-green-600 font-medium'>
                                  {newVal}
                                </span>
                              ))}
                            {!Array.isArray(change.new_value) && change.new_value && (
                              <span className='text-green-600 font-medium'>{JSON.stringify(change.new_value)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data field changes */}
                {item.data_field_changes && item.data_field_changes.length > 0 && (
                  <div className='mt-2'>
                    <div className='text-xs font-semibold text-gray-600 mb-1'>Data Fields:</div>
                    <div className='space-y-2'>
                      {item.data_field_changes.map((change, idx) => (
                        <div key={idx} className='text-sm'>
                          <span className='font-medium'>{change.field}:</span>
                          <div className='ml-2 space-y-1'>
                            {Object.entries(change.old_value || {}).map(([subKey, subVal]) => (
                              <div key={subKey} className='text-xs'>
                                <span className='font-medium'>{subKey}:</span>
                                <span className='line-through text-gray-400 ml-1'>{JSON.stringify(subVal)}</span>
                              </div>
                            ))}
                            {Object.entries(change.new_value || {}).map(([subKey, subVal]) => (
                              <div key={subKey} className='text-xs'>
                                <span className='font-medium'>{subKey}:</span>
                                <span className='text-green-600 font-medium ml-1'>{JSON.stringify(subVal)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      // 普通数组处理
      return value.map((item, index) => (
        <div key={index} className={`ml-${depth * 4} mt-1`}>
          • {formatValue(item, depth + 1)}
        </div>
      ));
    } else if (isFieldUpdateObject(value)) {
      // 处理字段更新对象（field_updates 中的单个字段变化）
      const formatArrayItem = item => {
        if (typeof item === 'string' || typeof item === 'number') {
          return item.toString();
        }
        return JSON.stringify(item);
      };

      return (
        <div className={`ml-${depth * 4} space-y-1`}>
          <span className='font-medium'>{value.field}:</span>
          <div className='ml-2 flex flex-wrap gap-2'>
            {Array.isArray(value.old_value) &&
              value.old_value.map((item, idx) => (
                <span key={`old-${idx}`} className='line-through text-gray-400 text-sm'>
                  {formatArrayItem(item)}
                </span>
              ))}
            {Array.isArray(value.new_value) &&
              value.new_value.map((item, idx) => (
                <span key={`new-${idx}`} className='text-green-600 font-medium text-sm'>
                  {formatArrayItem(item)}
                </span>
              ))}
          </div>
        </div>
      );
    } else if (isVersionControlObject(value)) {
      // 特殊处理新旧值对比对象 - 紧凑显示，用删除线表示旧值
      const formatArrayItem = item => {
        if (typeof item === 'string' || typeof item === 'number') {
          return item.toString();
        }
        return JSON.stringify(item);
      };

      return (
        <div className={`ml-${depth * 4} space-y-1`}>
          {/* 时间戳 */}
          {value.time_stamp && (
            <div className='text-xs text-gray-400 italic'>⏰ {formatTimestamp(value.time_stamp)}</div>
          )}

          {/* 数组类型的值 */}
          {Array.isArray(value.oldvalue) || Array.isArray(value.newvalue) ? (
            <div className='space-y-1'>
              {/* 显示所有新旧值的对比 */}
              <div className='flex flex-wrap gap-2'>
                {/* 旧值列表 */}
                {value.oldvalue &&
                  value.oldvalue.map((item, idx) => (
                    <span key={`old-${idx}`} className='line-through text-gray-400 text-sm'>
                      {formatArrayItem(item)}
                    </span>
                  ))}
                {/* 新值列表 */}
                {value.newvalue &&
                  value.newvalue.map((item, idx) => (
                    <span key={`new-${idx}`} className='text-green-600 font-medium text-sm'>
                      {formatArrayItem(item)}
                    </span>
                  ))}
              </div>
            </div>
          ) : (
            /* 单个值的对比 */
            <div className='space-y-1'>
              {value.oldvalue && (
                <div>
                  <span className='line-through text-gray-400 text-sm'>{formatValue(value.oldvalue, depth + 1)}</span>
                </div>
              )}
              {value.newvalue && (
                <div>
                  <span className='text-green-600 font-medium text-sm'>{formatValue(value.newvalue, depth + 1)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    } else if (typeof value === 'object') {
      // 如果是普通对象，显示为键值对
      return Object.entries(value).map(([subKey, subValue]) => (
        <div key={subKey} className={`ml-${depth * 4} mt-1`}>
          <span className='font-medium'>{subKey}:</span> {formatValue(subValue, depth + 1)}
        </div>
      ));
    } else if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    } else {
      return value.toString();
    }
  };

  return (
    <>
      <div className='fixed inset-0 bg-black bg-opacity-30 z-40' onClick={onClose}></div>
      <div
        className={`fixed right-0 top-0 h-full ${width} bg-white shadow-lg z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out`}
      >
        <div className='p-6'>
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-xl font-bold'>{title}</h2>
            <button onClick={onClose} className='text-gray-500 hover:text-gray-700 text-xl font-bold' title='关闭'>
              ✕
            </button>
          </div>

          <div className='space-y-4'>
            {Object.entries(summary).map(([key, value]) => (
              <div key={key} className='border-b pb-2'>
                <h3 className='font-semibold text-gray-700 capitalize'>{key.replace(/_/g, ' ')}:</h3>
                <div className='text-gray-600 mt-1'>{formatValue(value)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailDrawer;
